import type { PurchaseOrder } from '../../../services/purchaseOrderService';
import type {
  HiredEquipmentAnchor,
  HiredEquipmentGridRow,
  HiredEquipmentPoResource,
  PurchaseOrderOption,
  TaskOption,
} from '../types';
import { LUMP_SUM_RESOURCE_LABEL, LUMP_SUM_UNIT } from '../types';
import { defaultFuelSurchargeEligibleFromVehicleType } from './fuelSurchargeDefault';
import { defaultOvertimeEligible } from './overtime';

export function newClientId(): string {
  return `he-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Task number + description only (closed combobox value / primary option line). */
export function formatTaskHead(task: {
  id: number;
  taskNumber?: string | null;
  taskDescription?: string | null;
  description?: string | null;
  name?: string | null;
}): string {
  const desc = task.taskDescription || task.description || task.name || '';
  if (task.taskNumber) return desc ? `${task.taskNumber} — ${desc}` : task.taskNumber;
  return desc || `Task ${task.id}`;
}

export function formatTaskLabel(task: {
  id: number;
  taskNumber?: string | null;
  taskDescription?: string | null;
  description?: string | null;
  name?: string | null;
  projectName?: string | null;
}): string {
  const head = formatTaskHead(task);
  const project = task.projectName?.trim();
  return project ? `${head} (${project})` : head;
}

/** Map a search / light-task payload onto the hired-equipment picker option. */
export function toTaskOption(task: {
  id: number;
  taskNumber?: string | null;
  description?: string | null;
  name?: string | null;
  projectName?: string | null;
  project?: { name?: string | null } | null;
  type?: string | null;
  mode?: string | null;
}): TaskOption {
  return {
    id: task.id,
    description: task.description || task.name || '',
    name: task.name ?? undefined,
    taskNumber: task.taskNumber ?? undefined,
    projectName: task.projectName ?? task.project?.name ?? null,
    type: task.type ?? null,
    mode: task.mode ?? null,
  };
}

/**
 * A Routine task in specific mode — the kind hired equipment duplicates into its own copy.
 *
 * A null/absent `mode` counts as specific: the column is `String? @default("specific")`, so rows
 * written before the default existed carry null and are not standing.
 */
export function isRoutineSpecificTask(task: { type?: string | null; mode?: string | null }): boolean {
  if ((task.type || '').toLowerCase() !== 'routine') return false;
  return (task.mode ?? 'specific').toLowerCase() === 'specific';
}

/**
 * Point a row at a freshly cloned task.
 *
 * Deliberately not `nextRowAfterTaskChange`: that clears the PO and resource when the row's PO
 * does not carry the task, and a brand-new clone never does — so routing a duplicate through it
 * would strip the PO off every duplicated PO line, losing the rate and resource linkage that is
 * the whole point of duplicating one. The task is the only thing that changes here.
 */
export function applyClonedTaskToRow(
  row: HiredEquipmentGridRow,
  clone: TaskOption,
): HiredEquipmentGridRow {
  return {
    ...row,
    taskId: clone.id,
    taskLabel: formatTaskLabel(clone),
    taskCreatedInSession: true,
  };
}

/** Catalogue VendorResource id when the PO line is linked; otherwise the PO line id. */
export function poGridResourceId(resource: HiredEquipmentPoResource): number {
  return resource.equipmentMasterId ?? resource.id;
}

export function emptyRowFromAnchor(anchor: HiredEquipmentAnchor): HiredEquipmentGridRow {
  return {
    clientId: newClientId(),
    date: todayIsoDate(),
    vendorId: anchor.lockedVendorId ?? null,
    vendorName: anchor.lockedVendorName ?? null,
    poId: anchor.lockedPoId ?? null,
    poNumber: anchor.lockedPoNumber ?? null,
    taskId: anchor.lockedTaskId ?? null,
    taskLabel: anchor.lockedTaskLabel ?? null,
    resourceId: null,
    resourceLabel: null,
    qty: null,
    unit: '',
    rate: null,
    vendorMaterial: false,
    vendorMaterialNotes: '',
    fuelSurchargeEligible: null,
    fuelSurchargeOverridePerHour: null,
    overtimeEligible: false,
    resourceOvertimeEligible: false,
    ticketNumber: '',
    lumpSum: false,
  };
}

/**
 * Force the lump-sum shape onto a row. A lump sum carries its amount in `rate`
 * with qty 1, so the whole qty x rate pipeline (totals, PO balance, efforts)
 * needs no special case; everything a lump sum must NOT have is cleared here.
 *
 * Run from the grid's own `patch` so no field handler - vendor, PO, task, or a
 * future one - can leave a lump sum row half-converted.
 */
export function normalizeLumpSumRow(row: HiredEquipmentGridRow): HiredEquipmentGridRow {
  if (!row.lumpSum) return row;
  return {
    ...row,
    qty: 1,
    unit: LUMP_SUM_UNIT,
    resourceId: null,
    resourceLabel: LUMP_SUM_RESOURCE_LABEL,
    vehicleType: null,
    fuelSurchargeEligible: false,
    fuelSurchargeOverridePerHour: null,
    overtimeEligible: false,
    resourceOvertimeEligible: false,
  };
}

/** The editable dollar figure on a lump sum line. Stored in `rate` (qty is 1). */
export function lumpSumAmount(row: HiredEquipmentGridRow): number | null {
  return row.rate;
}

export function lumpSumRowFromAnchor(anchor: HiredEquipmentAnchor): HiredEquipmentGridRow {
  return normalizeLumpSumRow({ ...emptyRowFromAnchor(anchor), lumpSum: true });
}

export function duplicateRow(row: HiredEquipmentGridRow): HiredEquipmentGridRow {
  // Flagged until the grid sees an edit to this row (any patch clears it) - a copy still
  // carrying yesterday's date or someone else's quantity is easy to submit unreviewed
  // otherwise, since it looks identical to a line someone actually filled in.
  return { ...row, clientId: newClientId(), attachment: null, duplicatedPending: true };
}

/**
 * Map a Module A purchase order onto HE prefill rows.
 *
 * Tasks sit on the line (`taskId` / `task`), not the PO header. Copy each
 * line's own task; leave it blank when that line has none.
 */
export function mapPurchaseOrderToPoResources(
  po: PurchaseOrder,
  rateByResourceId: Map<number, number | null> = new Map(),
  vehicleTypeByResourceId: Map<number, string | null> = new Map(),
  overtimeEligibleByResourceId: Map<number, boolean> = new Map(),
  unitByResourceId: Map<number, string | null> = new Map(),
): HiredEquipmentPoResource[] {
  return (po.lineItems ?? []).map((line) => {
    const catalogueId = line.resourceId ?? null;
    const task = line.task ?? null;
    return {
      id: line.id,
      poId: po.id,
      description: line.description || line.resource?.name || `Line ${line.id}`,
      equipmentMasterId: catalogueId,
      rate: catalogueId != null ? (rateByResourceId.get(catalogueId) ?? null) : null,
      unitOfMeasurement:
        line.unit
        ?? line.resource?.unit
        ?? (catalogueId != null ? (unitByResourceId.get(catalogueId) ?? null) : null),
      qty: line.quantity,
      taskId: line.taskId ?? task?.id ?? null,
      taskNumber: task?.taskNumber ?? null,
      taskDescription: task?.name ?? null,
      vehicleType: catalogueId != null ? (vehicleTypeByResourceId.get(catalogueId) ?? null) : null,
      overtimeEligible: catalogueId != null ? (overtimeEligibleByResourceId.get(catalogueId) ?? false) : false,
    };
  });
}

function taskLabelFromResource(resource: HiredEquipmentPoResource): string | null {
  const taskId = resource.taskId;
  if (taskId == null && !resource.taskDescription && !resource.taskNumber && !resource.taskLabel) {
    return null;
  }
  if (resource.taskLabel) return resource.taskLabel;
  if (taskId == null) return resource.taskDescription ?? null;
  return formatTaskLabel({
    id: taskId,
    taskNumber: resource.taskNumber,
    taskDescription: resource.taskDescription,
  });
}

/**
 * Map each PO resource onto its own grid row.
 *
 * THE PRE-FILL DEFECT (tasks blank, rate only on first line) lived in the
 * previous mapper in HiredEquipmentGridPage, which only copied
 * `resource.id` / `resource.description` and dropped per-item `taskId`/`rate`.
 * A sibling pattern of the same class of bug is hoisting `const rate = resources[0].rate`
 * (or a PO-level default) outside the loop and writing it onto every row.
 *
 * This function reads `resource.taskId` and `resource.rate` **inside** the map
 * callback, from that resource, every time. Do not "optimize" by lifting them.
 */
export function rowsFromPoResources(
  anchor: HiredEquipmentAnchor,
  resources: HiredEquipmentPoResource[],
): HiredEquipmentGridRow[] {
  return resources.map((resource) => {
    const taskId = resource.taskId ?? anchor.lockedTaskId ?? null;
    return {
      ...emptyRowFromAnchor(anchor),
      resourceId: poGridResourceId(resource),
      resourceLabel: resource.description,
      qty: resource.qty ?? null,
      unit: resource.unitOfMeasurement ?? '',
      rate: resource.rate ?? null,
      vehicleType: resource.vehicleType ?? null,
      fuelSurchargeEligible: defaultFuelSurchargeEligibleFromVehicleType(resource.vehicleType),
      overtimeEligible: defaultOvertimeEligible(resource.overtimeEligible, resource.unitOfMeasurement),
      resourceOvertimeEligible: resource.overtimeEligible === true,
      taskId,
      taskLabel: taskLabelFromResource(resource) ?? (anchor.lockedTaskLabel ?? null),
    };
  });
}

function normalizeResourceLabel(label: string | null | undefined): string {
  return (label || '').trim().toLowerCase();
}

/**
 * Resolve the locked PO id when the form only persisted the PO number (approvals
 * detail view) rather than the numeric id from the new-submission anchor.
 */
export function resolveLockedPoId(
  anchor: HiredEquipmentAnchor,
  rows: HiredEquipmentGridRow[],
  purchaseOrders: PurchaseOrderOption[],
): number | null {
  if (anchor.lockedPoId != null) return anchor.lockedPoId;
  const poNumber = anchor.lockedPoNumber?.trim();
  if (!poNumber) return null;
  const rowPoId = rows.find((row) => row.poNumber === poNumber && row.poId != null)?.poId;
  if (rowPoId != null) return rowPoId;
  return purchaseOrders.find((po) => po.poNumber === poNumber)?.id ?? null;
}

/**
 * Re-add PO resources the user removed. De-dupes by PO line id and by resource
 * label (form-backed rows often lack resourceId) and appends only missing lines.
 */
export function appendMissingPoResources(
  anchor: HiredEquipmentAnchor,
  existing: HiredEquipmentGridRow[],
  resources: HiredEquipmentPoResource[],
): HiredEquipmentGridRow[] {
  const presentIds = new Set(
    existing.map((row) => row.resourceId).filter((id): id is number => id != null),
  );
  const presentLabels = new Set(
    existing
      .map((row) => normalizeResourceLabel(row.resourceLabel))
      .filter((label) => label.length > 0),
  );
  const missing = resources.filter((resource) => {
    if (presentIds.has(resource.id) || presentIds.has(poGridResourceId(resource))) return false;
    const label = normalizeResourceLabel(resource.description);
    return !(label && presentLabels.has(label));
  });
  if (missing.length === 0) return existing;
  return [...existing, ...rowsFromPoResources(anchor, missing)];
}
